// src/pages/admin/LeadsPage.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Loader, AlertCircle, Phone, User, Filter, MessageCircle, Calendar, Trash2, ArrowUpDown } from 'lucide-react';

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterSubject, setFilterSubject] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' for newest first, 'asc' for oldest first

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // טעינת תחומים
      const subjectsSnapshot = await getDocs(collection(db, 'subjects'));
      const subjectsData = subjectsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSubjects(subjectsData);

      // טעינת לידים
      const leadsSnapshot = await getDocs(collection(db, 'leads'));
      const leadsData = leadsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLeads(leadsData);
    } catch (err) {
      setError('אירעה שגיאה בטעינת הנתונים');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getSubjectTitle = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject?.name || 'לא מוגדר';
  };

  const filteredLeads = filterSubject 
    ? leads.filter(lead => lead.subject === filterSubject)
    : leads;

  // מיון לפי תאריך יצירה
  const sortedLeads = [...filteredLeads].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const formatPhoneNumber = (phone) => {
    const phoneString = String(phone);
    return phoneString.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
  };

  const handleDelete = async (leadId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק פנייה זו?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(db, 'leads', leadId));
        setLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));
        alert('הפנייה נמחקה בהצלחה');
      } catch (err) {
        setError('אירעה שגיאה במחיקת הפנייה');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-red-500">
        <AlertCircle className="w-6 h-6 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  // תצוגת דסקטופ
  const DesktopView = () => (
    <div className="hidden md:block overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-gray-50">
          <tr className="text-right">
            <th className="px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">שם מלא</th>
            <th className="px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">טלפון</th>
            <th className="px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">תחום</th>
            <th className="px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">
              <div className="flex items-center justify-end cursor-pointer" onClick={toggleSortOrder}>
                <span>תאריך</span>
                <ArrowUpDown className="w-4 h-4 mr-1" />
              </div>
            </th>
            <th className="px-6 py-3 text-xs font-medium text-gray-500 tracking-wider">פעולות</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sortedLeads.map(lead => (
            <tr key={lead.id} className="hover:bg-gray-50">
              <td className="px-6 py-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900">{lead.fullName}</span>
                </div>
              </td>
              <td className="px-6 py-3">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a href={`tel:${lead.phone}`} className="text-sm text-blue-600 hover:text-blue-800 ltr" dir="ltr">
                    {formatPhoneNumber(lead.phone)}
                  </a>
                </div>
              </td>
              <td className="px-6 py-3">
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  {getSubjectTitle(lead.subject)}
                </span>
              </td>
              <td className="px-6 py-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600">{formatDate(lead.createdAt)}</span>
                </div>
              </td>
              <td className="px-6 py-3">
                <div className="flex items-center gap-3">
                  <a 
                    href={`https://wa.me/${lead.phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-green-600 hover:text-green-800"
                  >
                    פתח בוואטסאפ
                  </a>
                  <button
                    onClick={() => handleDelete(lead.id)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // תצוגת מובייל
  const MobileView = () => (
    <div className="md:hidden space-y-4">
      {sortedLeads.map(lead => (
        <div key={lead.id} className="bg-white p-4 rounded-lg shadow">
          <div className="space-y-3">
            {/* שם */}
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-gray-900 font-medium">{lead.fullName}</span>
            </div>
            
            {/* תחום */}
            <div className="flex items-center justify-between">
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                {getSubjectTitle(lead.subject)}
              </span>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-600">{formatDate(lead.createdAt)}</span>
              </div>
            </div>

            {/* פעולות */}
            <div className="flex items-center gap-4 pt-2 border-t">
              <a 
                href={`tel:${lead.phone}`}
                className="flex items-center gap-1 text-blue-600"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm" dir="ltr">{formatPhoneNumber(lead.phone)}</span>
              </a>
              <a 
                href={`https://wa.me/${lead.phone}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-green-600"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm">וואטסאפ</span>
              </a>
              <button
                onClick={() => handleDelete(lead.id)}
                className="flex items-center gap-1 text-red-600"
              >
                <Trash2 className="w-4 h-4" />
                <span className="text-sm">מחק</span>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-xl md:text-2xl font-bold">צפייה בפניות</h1>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full md:w-auto">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Filter className="w-4 h-4 text-gray-600" />
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
              className="p-2 border rounded-md text-sm w-full md:w-auto"
            >
              <option value="">כל התחומים</option>
              {subjects.map(subject => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={toggleSortOrder} 
              className="flex items-center gap-1 text-blue-600 text-sm"
            >
              <ArrowUpDown className="w-4 h-4" />
              {sortOrder === 'desc' ? 'מהחדש לישן' : 'מהישן לחדש'}
            </button>
          </div>
          <div className="text-gray-600 text-sm">
            סה"כ: {sortedLeads.length} פניות
          </div>
        </div>
      </div>

      {/* תצוגת טעינה */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center min-h-[400px] text-red-500">
          <AlertCircle className="w-6 h-6 mr-2" />
          <span>{error}</span>
        </div>
      ) : (
        <>
          <DesktopView />
          <MobileView />

          {sortedLeads.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-500">אין פניות להצגה</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LeadsPage;